import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { Badge, Logo, TopNav, type TopNavItem } from '@tempmee/react';
import { routes } from '~/routes';
import { NAVBAR_HEIGHT } from '~/constants/layout';
import { logOut, showMessage } from '../../actions';
import history from '../../browserHistory';
import {
  fireMenuBarAccountClicked,
  fireMenuBarHelpClicked,
  fireMenuBarInboxClicked,
} from '~/posthogs/events';
import { useInboxCount } from '../Inbox/hooks/useInboxCount';

interface ITopNavBarProps {
  hasOfficeSelected?: boolean;
}

export default function TopNavBar({ hasOfficeSelected = true }: ITopNavBarProps) {
  const location = useLocation();
  const dispatch = useDispatch();
  const { totalInbox } = useInboxCount();

  const getHelp = () => {
    if (location.pathname === '/registration') {
      dispatch(
        showMessage({
          title: 'Error',
          message: 'Please finish your registration to access the rest of the platform.',
          isError: true,
        }),
      );
    } else {
      fireMenuBarHelpClicked();
      history.push(routes.help);
    }
  };

  const goToInbox = () => {
    if (location.pathname === '/registration') {
      dispatch(
        showMessage({
          title: 'Error',
          message: 'Please finish your registration to access the rest of the platform.',
          isError: true,
        }),
      );
    } else {
      fireMenuBarInboxClicked();
      history.push('/dashboard/inbox');
    }
  };

  const goToAccount = () => {
    fireMenuBarAccountClicked();
    history.push('/account');
  };

  const handleLogout = () => {
    sessionStorage.clear();
    dispatch(logOut());
  };

  // Map the active nav item from the current route (controlled `value`).
  const activeValue = (() => {
    if (location.pathname === '/dashboard/inbox') return 'inbox';
    if (location.pathname === routes.help) return 'help';
    if (location.pathname === '/account') return 'account';
    return undefined;
  })();

  const handleNavChange = (value: string) => {
    switch (value) {
      case 'inbox':
        goToInbox();
        break;
      case 'help':
        getHelp();
        break;
      case 'account':
        goToAccount();
        break;
      case 'logout':
        handleLogout();
        break;
    }
  };

  const isRegistration = location.pathname.includes('registration');

  const items: TopNavItem[] = (() => {
    if (isRegistration) return [];
    if (!hasOfficeSelected) {
      return [{ value: 'logout', label: 'Log Out', 'data-cy': 'top-nav-logout-menu' }];
    }
    return [
      {
        value: 'inbox',
        label: 'Inbox',
        'data-cy': 'top-nav-inbox-menu',
        badge: totalInbox > 0 ? <Badge severity="flagged" count={totalInbox} /> : undefined,
      },
      { value: 'help', label: 'Help', 'data-cy': 'top-nav-help-menu' },
      { value: 'account', label: 'Account', 'data-cy': 'top-nav-account-menu' },
    ];
  })();

  return (
    <div style={{ height: NAVBAR_HEIGHT }}>
      <TopNav
        color="dark"
        value={activeValue ?? ''}
        onChange={handleNavChange}
        items={items}
        logo={
          <Logo
            tone="light"
            mark="full"
            height={44}
            style={{ cursor: 'pointer' }}
            onClick={() => history.push('/')}
          />
        }
      />
    </div>
  );
}
